export { bgDesc2style };

/**
 * @typedef {Object} RawBackgroundDescription
 * @property {string} data Contain arbitrary data related to type: url, color, gradient, etc.
 * @property {"fill"|"gradient"|"image"|"video"} type
 * @property {string} [fit] non-standard fit, see remapFit
 * @todo Does it fit still in use? See no one in promo creative.
 * @property {boolean} isFillAllSpace no idea what for
 * @property {number} [opacity]
 */

/**
 * @typedef {Object} BackgroundDescription
 * @property {string} id
 * @property {{background: string, opacity, backgroundRepeat: (string)}} style
 * @property {React.ReactElement} [children]
 */
/**
 * Based (e.g. cloned from) bridges' schemas/rewirePollSchema
 * @param {RawBackgroundDescription} bgDescription
 * @returns {BackgroundDescription}
 */
function bgDesc2style(bgDescription) {
  const { id, type, fit, opacity } = bgDescription;
  const style = {
    opacity,
    background: getBgRuleGenerator(type)(bgDescription),
    backgroundRepeat: fit === 'Tile' ? 'repeat' : 'no-repeat',
  };

  if (type === 'video') {
    console.warn('video as background feature disabled for a while');
    /*
    return {
      id,
      style,
      children: <video autoPlay loop muted src={data} />
    };
*/
  }

  if (type === 'gradient') {
    // gradient already has opacity after stringification
    // check out stringifyGradient func
    delete style.opacity;
  }

  return {
    id,
    style,
  };
}

function getBgRuleGenerator(type) {
  switch (type) {
    case 'fill': {
      return ({ data }) => `linear-gradient(${data}, ${data})`;
    }
    case 'var-fill': {
      return ({ data }) => `var(--fill-color, ${data})`;
    }
    case 'gradient': {
      return ({ data, opacity }) => {
        try {
          return stringifyGradient(data, opacity);
        } catch {

          console.warn(
            'The whole MAGENTA screen? Feel free to validate gradient transformation.',
          );
          return () => `linear-gradient(magenta, magenta)`;
        }
      };
    }
    case 'image': {
      const remapFit = {
        Fit: 'center / contain',
        Fill: 'center / cover',
        Stretch: 'center / 100% 100%',
        Tile: '0',
      };
      return ({ data, fit }) =>
        `${remapFit[fit]} url(${data.replace(/^https?:/, '')})`;
    }
    default: {
      return () => undefined;
    }
  }
}

/*
// may be latter, for another case
const rgbPattern = /rgba?\((?<red>\d+),\s?(?<green>\d+),\s?(?<blue>\d+)(?:,\s?(?<alpha>[\d.]+%?))?\)/;
*/

const rgbSimplePattern =
  /rgba?\((?<rgb>\d+,\s?\d+,\s?\d+)(?:,\s?(?<alpha>[\d.]+%?))?\)/;

function stringifyGradient(data, opacity = 1) {
  /*
  Due to applies gradients as background image
   it is necessary to hacky apply "layer" opacity as color opacity
   */
  const applyOpacity =
    opacity === 1
      ? (color) => color
      : (color) => {
          const matches = rgbSimplePattern.exec(color);
          if (!matches) {
            console.warn('no-op: can not process color ', color);
            return color;
          }

          const { groups: { rgb, alpha = '1' } = {} } = matches;

          if (rgb === undefined) {
            console.warn('no-op: can not parse color ', color);
            return color;
          }

          const ownOpacity =
            parseFloat(alpha) / (alpha.includes('%') ? 100 : 1);

          return `rgba(${rgb}, ${ownOpacity * opacity})`;
        };
  /**
   * @typedef {Object} GradientStopDescription
   * @property {string} color
   * @property {number} percent
   */
  const {
    /** @type {[GradientStopDescription]} */
    colors,
    /** @type {string} */
    direction,
    /** @type {string} */
    type,
  } = JSON.parse(data);

  const stops = colors
    .sort((c1, c2) => c1.percent - c2.percent)
    .map(({ percent, color }) => `${applyOpacity(color)} ${percent}%`)
    .join(',');

  return `center / cover no-repeat ${type}(${direction}, ${stops})`;
}
